import { Add, Delete, Download, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  IconButton,
} from '@mui/material'
import { CenteringBox } from 'components/CenteringBox'
import { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogWorkRateForm } from './dialog-new-work-rate-form'
import { WorkRatesTableRow } from 'pages/tender/lot/work-rates-table-row'
import {
  procurementsApi,
  useCreateProcurementMutation,
  useDeleteProcurementMutation,
  useProcurementsSearchQuery,
} from 'services/procurements'
import { useParams } from 'react-router-dom'
import { TablePagination } from 'components/TablePagination'
import { useQueryState } from 'react-router-use-location-state'
import { IProcurement } from 'types/procurements'
import { v4 as uuidv4 } from 'uuid'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { toast } from 'react-toastify'
import { downloadExcel } from 'utils/download-file'
import { FileUpload } from 'components/FileUpload'
import axiosInstance from 'store/axiosInstance'
import { useDispatch } from 'react-redux'
import { procurementRequirementApi } from 'services/lot/procurement-requirements'
import { AddWorkRateTableRow } from 'pages/tender/lot/add-work-rate-table-row'

const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
  width: '100%',
  '& th, & td': {
    border: '1px solid #dddddd !important',
  },
  '& th': {
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
}))

type Props = {
  procurementRequirementId?: string
  isDisabled: boolean
}

const WorkRatesTableComponent = ({
  procurementRequirementId,
  isDisabled,
}: Props) => {
  const [workRateFormOpen, setWorkRateFormOpen] = useState(false)
  const [isAddOpen, setAddOpen] = useState(false)
  const [editingItemIndex, setEditingItemIndex] = useState(-1)
  const { t } = useTranslation()
  const { lotId } = useParams()
  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10
  const dispatch = useDispatch()

  const [createOrUpdateProcurement, { isLoading }] = useCreateProcurementMutation()
  const [deleteProcurement, { isLoading: isDeleteProcurementLoading }] =
    useDeleteProcurementMutation()

  const args = {
    page,
    size: rowsPerPage,
    lotId,
  } as const

  const {
    data,
    isLoading: isProcurementDataLoading,
    isError: isProcurementDataError,
    refetch,
  } = useProcurementsSearchQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  const handleEditClick = useCallback((idx: number) => {
    setWorkRateFormOpen(true)
    setEditingItemIndex(idx)
  }, [])

  const handleFormClose = useCallback(() => {
    setWorkRateFormOpen(false)
    setEditingItemIndex(-1)
  }, [])

  const handleFormSubmit = useCallback(
    async (newWorkRate: IProcurement) => {
      let data
      if (newWorkRate.id) {
        data = await createOrUpdateProcurement({
          ...newWorkRate,
        })
      } else {
        data = await createOrUpdateProcurement({
          ...newWorkRate,
          id: uuidv4(),
          procurementRequirementId,
        })
      }

      if ('error' in data) {
        if ('status' in data.error && data.error.status === 400) {
          toast.warning(t('make-sure-fields-correct'))
        } else {
          toast.error(t('failedToCreate'))
        }
      }

      if (!('error' in data)) {
        toast.success(t('operation-success'))
        refetch()
        dispatch(
          procurementRequirementApi.util.invalidateTags(['AllProcurementItems'])
        )
      }
    },
    [editingItemIndex]
  )

  const handleAddClick = useCallback(() => {
    setAddOpen(true)
  }, [])

  const handleDownloadTemplate = async () => {
    downloadExcel({
      fileName: 'satinalma_predmeti_cedvel',
      url: `/procurement-requirements/${procurementRequirementId}/items/download-template`,
    })
  }

  const handleUploadTemplate = async (files: any) => {
    try {
      const response = await axiosInstance.post(
        `/procurement-requirements/${procurementRequirementId}/items/upload-template`,
        files,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      dispatch(procurementsApi.util.invalidateTags(['ProcurementItems']))
    } catch (error) {
      console.error('Error uploading file:', error)
    }
  }

  if (isProcurementDataLoading) {
    return <Loading fullPage={false} />
  }
  if (isProcurementDataError) {
    return <Error />
  }

  return (
    <>
      {workRateFormOpen && (
        <DialogWorkRateForm
          handleClose={handleFormClose}
          workRate={data?.items[editingItemIndex]}
          onAdd={handleFormSubmit}
        />
      )}
      <Box my={2}>
        <Typography variant="h6" fontWeight="bolder">
          {t('work-rate-sheet')}
        </Typography>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box
            display="flex"
            justifyContent="flex-end"
            marginBottom={1}
            marginTop={1}
            gap={2}
          >
            <FileUpload
              text={t('add-sheet')}
              handleAsRawFile={(file) => handleUploadTemplate(file)}
              sendToApi={false}
              isMulti
              isDisabled={isDisabled}
            />
            <Button
              onClick={handleDownloadTemplate}
              variant="text"
              startIcon={<Download />}
              disabled={isDisabled}
            >
              {t('download-excel-template')}
            </Button>
          </Box>
          <TableContainer>
            <StyledTable sx={{ minWidth: 650, borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>{t('name')}</TableCell>
                  <TableCell>{t('description')}</TableCell>
                  <TableCell>{t('quantity')}</TableCell>
                  <TableCell>{t('unit')}</TableCell>
                  <TableCell>{t('file')}</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.items.map((workRate, idx) => (
                  <WorkRatesTableRow
                    isDisabled={isDisabled}
                    workRate={workRate}
                    onAdd={handleFormSubmit}
                    refetch={refetch}
                    index={idx}
                    pagesInfo={data.info}
                    pageSize={rowsPerPage}
                    key={workRate.id}
                    onEditClick={handleEditClick}
                    onDeleteClick={async () => {
                      const d = await deleteProcurement({
                        procurementId: procurementRequirementId,
                        id: workRate.id,
                      })

                      if ('error' in d) {
                        if ('status' in d.error && d.error.status === 400) {
                          toast.warning(t('make-sure-fields-correct'))
                        } else {
                          toast.error(t('failedToCreate'))
                        }
                      }

                      if (!('error' in d)) {
                        toast.success(t('operation-success'))
                        dispatch(
                          procurementRequirementApi.util.invalidateTags([
                            'AllProcurementItems',
                          ])
                        )
                      }
                    }}
                  />
                ))}
                {isAddOpen && (
                  <AddWorkRateTableRow
                    disabled={isLoading}
                    onAdd={handleFormSubmit}
                    no={data?.items?.length ?? 1}
                    onClose={() => setAddOpen(false)}
                  />
                )}
              </TableBody>
            </StyledTable>
          </TableContainer>
        </Paper>

        <CenteringBox my={2}>
          <Button
            disabled={isDisabled}
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handleAddClick}
          >
            <Add />
          </Button>
        </CenteringBox>
        {data && data.info.totalRows !== 0 && (
          <TablePagination
            total={data.info.totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </Box>
    </>
  )
}

export const WorkRatesTable = memo(WorkRatesTableComponent)
